import { jsx as _jsx } from "react/jsx-runtime";
import colors from "@moodys/mdc-frontend.theming.colors";
import typographyStyles from "@moodys/mdc-frontend.theming.fonts/typography.module.scss";
import { alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
const roundedBorderRadius = 44;
const getLinkVariant = (paletteColor, theme) => ({
    backgroundColor: "transparent",
    color: paletteColor.main,
    padding: 0,
    boxShadow: "none",
    "&:hover, &:focus-visible": {
        textDecoration: "underline",
        color: paletteColor.light,
        backgroundColor: "transparent",
    },
    "&:active": {
        color: paletteColor.light,
        textDecoration: "underline",
    },
    "&:disabled": {
        color: theme.palette.action.disabled,
    },
});
const getRoundedVariant = (paletteColor, theme) => ({
    borderRadius: roundedBorderRadius,
    backgroundColor: paletteColor.main,
    color: paletteColor.contrastText,
    "&:hover, &:focus-visible": {
        backgroundColor: paletteColor.dark,
        boxShadow: "none",
    },
    "&:active": {
        backgroundColor: paletteColor.light,
        boxShadow: "none",
    },
    "&:disabled": {
        color: theme.palette.action.disabled,
        backgroundColor: theme.palette.action.disabledBackground,
    },
});
const getContainedVariant = (paletteColor) => ({
    backgroundColor: paletteColor.main,
    color: paletteColor.contrastText,
    boxShadow: "none",
    "&:hover, &:focus-visible": {
        backgroundColor: paletteColor.dark,
        boxShadow: "none",
    },
    "&:active": {
        backgroundColor: paletteColor.light,
        boxShadow: "none",
    },
    "&:disabled": {
        borderColor: "transparent",
    },
});
const getRoundedOutlineVariant = (paletteColor, theme) => ({
    borderRadius: roundedBorderRadius,
    color: paletteColor.main,
    borderWidth: 1,
    borderStyle: "solid",
    "&:not(:disabled)": {
        borderColor: paletteColor.main,
    },
    "&:hover": {
        backgroundColor: theme.palette.action.hover,
    },
    "&:active": {
        backgroundColor: theme.palette.action.focus,
    },
    "&:disabled": {
        color: theme.palette.action.disabled,
        backgroundColor: theme.palette.background.paper,
    },
});
const getTextVariant = (paletteColor, theme) => ({
    color: paletteColor.main,
    "&:hover": {
        backgroundColor: theme.palette.action.hover,
    },
    "&:active": {
        backgroundColor: theme.palette.action.focus,
    },
});
const getSecondaryVariant = (theme) => ({
    backgroundColor: theme.palette.action.focus,
    color: theme.palette.primary.main,
    "&.MuiButtonBase-root": {
        border: "1px solid",
        borderColor: "transparent", // Prevent a jump when toggling selected state
    },
    "&.MuiButtonBase-root:hover, &.MuiButtonBase-root.Mui-focusVisible": {
        backgroundColor: alpha(theme.palette.primary.main, 0.2),
    },
    "&.MuiButtonBase-root:active": {
        backgroundColor: alpha(theme.palette.primary.main, 0.3),
    },
    ".MuiChip-icon, .MuiChip-deleteIcon": {
        color: theme.palette.action.active,
    },
    ".MuiChip-deleteIcon:hover": {
        color: theme.palette.primary.main,
    },
});
const getSecondaryOutlineVariant = (theme) => ({
    ...getSecondaryVariant(theme),
    borderColor: theme.palette.primary.main,
});
const getOutlinedVariant = (theme) => ({
    borderColor: theme.palette.chipOutlinedBorder,
    "&.MuiButtonBase-root:active": {
        backgroundColor: alpha(theme.palette.primary.main, 0.12),
    },
});
export const componentThemes = {
    MuiAccordion: {
        defaultProps: {
            disableGutters: true,
        },
        styleOverrides: {
            root: ({ theme }) => ({
                boxShadow: "none",
                "&.Mui-disabled": {
                    backgroundColor: "inherit",
                },
                ".Mui-disabled": {
                    color: colors.globalGray400,
                },
                ".Mui-expanded": {
                    "&::before": {
                        opacity: 1,
                    },
                },
                "&:hover": {
                    color: theme.palette.text.secondary,
                },
            }),
        },
    },
    MuiAutocomplete: {
        variants: [
            {
                props: { size: "medium" },
                style: () => ({
                    ".MuiFormControl-root > .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
                        transform: "translate(14px, 10px) scale(1)",
                    },
                    ".MuiInputBase-root > .MuiInputBase-input": {
                        paddingBottom: 2,
                        paddingTop: 2,
                    },
                }),
            },
            {
                props: { size: "small" },
                style: () => ({
                    ".MuiFormControl-root > .MuiInputLabel-filled:not(.MuiInputLabel-shrink)": {
                        transform: "translate(12px, 13px) scale(1)",
                    },
                    ".MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
                        paddingBottom: 3,
                        paddingTop: 3,
                    },
                }),
            },
        ],
        styleOverrides: {
            root: () => ({
                ".MuiInputBase-root": {
                    height: "auto",
                },
            }),
        },
    },
    MuiListItemButton: {
        styleOverrides: {
            root: ({ theme }) => ({
                padding: "12px 16px",
                alignItems: "center",
                disabled: {
                    backgroundColor: "inherit",
                    color: colors.globalGray400,
                },
                ".MuiCheckbox-root": {
                    marginLeft: 0,
                },
                selected: {
                    backgroundColor: theme.palette.action.focus,
                },
                "&:focus-visible": {
                    outlineOffset: -2,
                    outline: `2px solid ${theme.palette.action.focusOutline}`,
                    backgroundColor: "inherit",
                },
                "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                },
            }),
        },
    },
    MuiListItemText: {
        styleOverrides: {
            root: () => ({
                margin: "0",
                boxShadow: "none",
                ".MuiTypography-root": {
                    margin: "0",
                    padding: "0",
                },
                disabled: {
                    color: colors.globalGray400,
                },
            }),
        },
    },
    MuiMenu: {
        defaultProps: {
            elevation: 5,
        },
        styleOverrides: {
            list: {
                margin: 0,
            },
        },
    },
    MuiMenuItem: {
        styleOverrides: {
            root: ({ theme }) => ({
                marginBottom: 0,
                selected: {
                    backgroundColor: theme.palette.action.focus,
                },
                "&:focus-visible": {
                    outlineOffset: -2,
                    outline: `2px solid ${theme.palette.action.focusOutline}`,
                    backgroundColor: "inherit",
                },
                disabled: {
                    color: colors.globalGray400,
                },
                "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                },
                [theme.breakpoints.down("sm")]: {
                    padding: "12px 16px",
                    minWidth: 0,
                },
            }),
        },
    },
    MuiAccordionSummary: {
        styleOverrides: {
            root: ({ theme }) => ({
                ".MuiAccordionSummary-content": {
                    color: "inherit",
                    ".MuiTypography-root.MuiTypography-body1": {
                        "&:first-of-type": {
                            fontWeight: typographyStyles.fontWeightSemiBold,
                        },
                        "&:last-child": {
                            color: "inherit",
                        },
                    },
                },
                "&:focus-visible": {
                    outlineOffset: -3,
                    outline: `2px solid ${theme.palette.action.focusOutline}`,
                    backgroundColor: "inherit",
                },
            }),
        },
    },
    MuiButtonBase: {
        defaultProps: {
            disableRipple: true,
        },
        styleOverrides: {
            root: ({ theme }) => ({
                "&:focus-visible": {
                    outlineOffset: 1,
                    outline: `2px solid ${theme.palette.action.focusOutline}`,
                },
                "&.MuiTabScrollButton-root": {
                    "&.Mui-disabled": {
                        opacity: 0.25,
                    },
                },
            }),
        },
    },
    MuiButtonGroup: {
        defaultProps: {
            disableRipple: true,
        },
        styleOverrides: {
            root: ({ theme }) => ({
                "button[disabled]": {
                    borderColor: theme.palette.outlinedBorder,
                },
            }),
        },
    },
    MuiButton: {
        variants: [
            {
                props: {
                    size: "small",
                },
                style: ({ theme }) => ({
                    fontSize: theme.typography.pxToRem(13),
                    padding: "4px 10px",
                }),
            },
            {
                props: {
                    size: "medium",
                },
                style: ({ theme }) => ({
                    fontSize: theme.typography.pxToRem(14),
                    padding: "6px 16px",
                }),
            },
            {
                props: {
                    size: "large",
                },
                style: ({ theme }) => ({
                    fontSize: theme.typography.pxToRem(15),
                    padding: "8px 22px",
                }),
            },
            {
                props: { variant: "rounded", color: "primary" },
                style: ({ theme }) => getRoundedVariant(theme.palette.primary, theme),
            },
            {
                props: { variant: "rounded", color: "secondary" },
                style: ({ theme }) => getRoundedVariant(theme.palette.secondary, theme),
            },
            {
                props: { variant: "contained", color: "primary" },
                style: ({ theme }) => getContainedVariant(theme.palette.primary),
            },
            {
                props: { variant: "contained", color: "secondary" },
                style: ({ theme }) => getContainedVariant(theme.palette.secondary),
            },
            {
                props: { variant: "roundedOutline", color: "primary" },
                style: ({ theme }) => getRoundedOutlineVariant(theme.palette.primary, theme),
            },
            {
                props: { variant: "roundedOutline", color: "secondary" },
                style: ({ theme }) => getRoundedOutlineVariant(theme.palette.secondary, theme),
            },
            {
                props: { variant: "text", color: "primary" },
                style: ({ theme }) => getTextVariant(theme.palette.primary, theme),
            },
            {
                props: { variant: "text", color: "secondary" },
                style: ({ theme }) => getTextVariant(theme.palette.secondary, theme),
            },
            {
                props: { variant: "linkVariant", color: "primary" },
                style: ({ theme }) => getLinkVariant(theme.palette.primary, theme),
            },
            {
                props: { variant: "linkVariant", color: "secondary" },
                style: ({ theme }) => getLinkVariant(theme.palette.secondary, theme),
            },
        ],
        defaultProps: {
            variant: "rounded",
            style: {
                textTransform: "capitalize",
            },
        },
    },
    MuiIconButton: {
        defaultProps: {
            size: "small",
        },
    },
    MuiChip: {
        defaultProps: {
            deleteIcon: _jsx(ClearIcon, {}),
            color: "primary",
            variant: "outlined",
        },
        styleOverrides: {
            root: ({ theme }) => ({
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                height: "auto",
                padding: "4px 8px",
                gap: 4,
                ".MuiSvgIcon-root": {
                    fontSize: "1rem",
                    alignSelf: "center",
                },
                ".MuiChip-label": {
                    paddingLeft: 0,
                    paddingRight: 0,
                },
                ".MuiChip-icon, .MuiChip-deleteIcon": {
                    margin: 0,
                },
                ".MuiChip-iconColorInfo, .MuiChip-iconColorWarning, .MuiChip-iconColorError, .MuiChip-iconColorSuccess": {
                    color: "currentColor",
                },
                ".MuiButton-root &": {
                    color: theme.palette.action.active,
                },
                "&.MuiButtonBase-root:active": {
                    boxShadow: "none",
                    backgroundColor: theme.palette.primary.light,
                },
            }),
        },
        variants: [
            {
                props: { variant: "outlined", color: "secondary" },
                style: ({ theme }) => getSecondaryOutlineVariant(theme),
            },
            {
                props: { color: "secondary" },
                style: ({ theme }) => getSecondaryVariant(theme),
            },
            {
                props: { variant: "outlined" },
                style: ({ theme }) => getOutlinedVariant(theme),
            },
            {
                props: { color: "success" },
                style: ({ theme }) => ({
                    color: theme.palette.success.dark,
                }),
            },
            {
                props: { size: "large" },
                style: () => ({
                    paddingTop: 8,
                    paddingBottom: 8,
                    height: 32,
                }),
            },
            {
                props: { size: "medium" },
                style: () => ({
                    paddingTop: 4,
                    paddingBottom: 4,
                    height: 24,
                }),
            },
            {
                props: { size: "small" },
                style: () => ({
                    padding: "2px 6px",
                    height: 20,
                }),
            },
        ],
    },
    MuiPaper: {
        defaultProps: {
            elevation: 3,
        },
    },
    MuiCard: {
        variants: [
            {
                props: { variant: "outlined" },
                style: {
                    border: "1px solid rgba(0, 0, 0, 0.23)",
                },
            },
        ],
    },
    MuiFormControlLabel: {
        styleOverrides: {
            root: () => ({
                ".MuiFormControlLabel-label": {
                    // this is coming from typography
                    marginBottom: 0,
                },
            }),
        },
    },
    MuiFormControl: {
        defaultProps: {
            size: "large",
        },
        styleOverrides: {
            root: () => ({
                "label, label > p": {
                    marginBottom: 0,
                },
                '[role="radiogroup"] + p.MuiFormHelperText-root': {
                    marginLeft: 0,
                },
            }),
        },
        variants: [
            {
                props: { size: "medium" },
                style: {
                    ".MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
                        lineHeight: "1.25",
                        transform: "translate(14px, 10px) scale(1)",
                    },
                },
            },
            {
                props: { size: "small" },
                style: {
                    ".MuiInputLabel-root": {
                        fontSize: "0.875em",
                        "&:not(.MuiInputLabel-shrink)": {
                            transform: "translate(14px, 6px) scale(1)",
                        },
                    },
                },
            },
        ],
    },
    MuiFormHelperText: {
        styleOverrides: {
            root: {
                fontWeight: 350,
                marginTop: 0,
            },
        },
    },
    MuiList: {
        styleOverrides: {
            root: {
                margin: "0 4px 6px",
            },
        },
    },
    MuiInputBase: {
        variants: [
            {
                props: { size: "large" },
                style: {
                    height: 56,
                },
            },
            {
                props: { size: "medium" },
                style: {
                    height: 40,
                },
            },
            {
                props: { size: "small" },
                style: {
                    fontSize: "0.875em",
                    height: 32,
                },
            },
            {
                props: { multiline: true },
                style: {
                    height: "auto",
                },
            },
        ],
    },
    MuiInputAdornment: {
        styleOverrides: {
            root: {
                "& .MuiIconButton-root:hover": {
                    background: "transparent",
                },
            },
        },
    },
    MuiOutlinedInput: {
        styleOverrides: {
            root: ({ theme }) => ({
                borderColor: theme.palette.primary.main,
                borderRadius: 4,
                borderWidth: 2,
                "&:hover:not(.Mui-focused):not(.Mui-error):not(.Mui-disabled)": {
                    ".MuiOutlinedInput-notchedOutline": {
                        borderColor: alpha(theme.palette.primary.main, 0.6),
                    },
                },
                "&.Mui-error": {
                    borderColor: theme.palette.error.main,
                    borderWidth: 1,
                },
            }),
            notchedOutline: ({ theme }) => ({
                ...(theme.palette.mode === "dark"
                    ? { borderColor: alpha(theme.palette.common.white, 0.5) }
                    : {}),
            }),
        },
    },
    MuiFilledInput: {
        styleOverrides: {
            root: ({ theme }) => ({
                // reserve space and prevent arrow from jumping
                border: "1px solid rgba(0, 0, 0, 0)",
                "&:hover": {
                    borderColor: alpha(theme.palette.primary.main, 0.12),
                },
                "&.Mui-focused": {
                    borderColor: alpha(theme.palette.primary.main, 0.7),
                },
                "&&&:before": {
                    border: 0,
                },
                "&&:after": {
                    border: 0,
                },
                borderBottomLeftRadius: 4,
                borderBottomRightRadius: 4,
            }),
        },
    },
    MuiTextField: {
        styleOverrides: {
            root: {
                ".MuiInputBase-root, .MuiTypography-root": { margin: 0 },
                "input::placeholder": {
                    opacity: 0.6,
                },
            },
        },
    },
    MuiRadio: {
        defaultProps: {
            disableRipple: true,
            disableFocusRipple: true,
            disableTouchRipple: true,
        },
        styleOverrides: {
            root: ({ theme }) => ({
                padding: 8,
                "&:hover": {
                    background: "transparent",
                    svg: {
                        color: theme.palette.primary.light,
                    },
                },
                "&.Mui-focusVisible svg": {
                    borderRadius: "50%",
                    outlineOffset: 0,
                    outline: `2px solid ${theme.palette.action.focusOutline}`,
                },
            }),
        },
    },
    MuiCheckbox: {
        defaultProps: {
            disableRipple: true,
            disableFocusRipple: true,
            disableTouchRipple: true,
        },
        styleOverrides: {
            root: ({ theme, ownerState: { color = "primary" } }) => ({
                alignSelf: "flex-start",
                padding: 0,
                paddingRight: 4,
                marginRight: 1,
                "input:focus-visible + svg": {
                    borderRadius: 4,
                    outlineOffset: -1,
                    outline: `2px solid ${theme.palette.action.focusOutline}`,
                },
                "&:not(.Mui-disabled)": {
                    "&.Mui-checked": {
                        color: theme.palette[color].main,
                        "&:hover": {
                            color: theme.palette[color].light,
                        },
                    },
                    "&:not(.Mui-checked)": {
                        "&:hover": {
                            color: theme.palette[color][theme.palette.mode === "dark" ? "light" : "dark"],
                        },
                    },
                },
            }),
        },
    },
    MuiSwitch: {
        defaultProps: {
            disableRipple: true,
            color: "secondary",
        },
        variants: [
            {
                props: { size: "small" },
                style: {
                    height: 22,
                    width: 34,
                    padding: 3,
                    marginRight: 5,
                    ".MuiSwitch-switchBase": {
                        height: 16,
                        width: 16,
                        top: 3,
                        left: 3,
                        ".MuiSwitch-thumb": {
                            height: 12,
                            width: 12,
                        },
                        "+ .MuiSwitch-track": {
                            width: 28,
                            height: 16,
                            borderRadius: 14,
                        },
                        "&.Mui-checked": {
                            transform: "translateX(12px)",
                        },
                    },
                },
            },
        ],
        styleOverrides: {
            root: ({ theme, ownerState: { color = "secondary" } }) => ({
                height: 26,
                width: 45,
                padding: 3,
                marginRight: 2,
                alignSelf: "flex-start",
                ".MuiSwitch-switchBase": {
                    height: 16,
                    width: 16,
                    top: 5,
                    left: 5,
                    padding: 0,
                    ".MuiSwitch-thumb": {
                        background: colors.globalWhite,
                        height: 16,
                        width: 16,
                    },
                    "+ .MuiSwitch-track": {
                        position: "relative",
                        width: 36,
                        height: 20,
                        borderRadius: 18,
                        background: "none",
                        opacity: "1 !important",
                        "&::after": {
                            background: theme.palette.primary.main,
                            transition: ".15s opacity ease, .15s background ease",
                            opacity: 0.26,
                            height: "100%",
                            width: "100%",
                            borderRadius: "inherit",
                            position: "absolute",
                            left: 0,
                            top: 0,
                            content: `" "`,
                        },
                    },
                    "&.Mui-checked": {
                        transform: "translateX(16px)",
                        "+ .MuiSwitch-track::after": {
                            background: theme.palette[color === "default" ? "secondary" : color].dark,
                            opacity: 1,
                        },
                    },
                    "&.Mui-disabled > .MuiSwitch-thumb": {
                        background: alpha(colors.globalWhite, 0.5),
                    },
                    "&.Mui-focusVisible + .MuiSwitch-track": {
                        outlineOffset: 1,
                        outline: `2px solid ${theme.palette.action.focusOutline}`,
                    },
                },
                "&:hover > .MuiSwitch-switchBase:not(.Mui-disabled)": {
                    "+ .MuiSwitch-track::after": {
                        background: theme.palette.primary.main,
                        opacity: 0.3,
                    },
                    "&.Mui-checked + .MuiSwitch-track::after": {
                        background: theme.palette[color === "default" ? "secondary" : color].main,
                        opacity: 1,
                    },
                },
            }),
        },
    },
    MuiDialog: {
        styleOverrides: {
            paper: ({ ownerState, theme }) => {
                const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
                return {
                    ...(ownerState &&
                        isSmall && {
                        margin: 0,
                        maxHeight: "100%",
                        maxWidth: "100%",
                    }),
                };
            },
        },
    },
    MuiDialogTitle: {
        defaultProps: {
            component: "h5",
        },
        styleOverrides: {
            root: ({ theme }) => ({
                "&.MuiTypography-root": {
                    fontSize: theme.typography.pxToRem(16),
                    fontWeight: 600,
                    marginBottom: 0,
                    minHeight: 16,
                },
                "&.MuiTypography-root > .MuiButtonBase-root": {
                    borderRadius: "2px",
                    float: "right",
                },
            }),
        },
    },
    MuiDialogContent: {
        styleOverrides: {
            root: {
                padding: "24px 24px 8px 24px",
                borderBottom: "unset",
            },
        },
    },
    MuiDialogActions: {
        styleOverrides: {
            root: {
                padding: 24,
            },
        },
    },
    MuiTab: {
        styleOverrides: {
            root: ({ theme }) => ({
                borderRadius: "4px",
                color: theme.palette.mode === "light"
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                disableRipple: true,
                minHeight: "32px",
                opacity: 1,
                padding: "8px 16px",
                whiteSpace: "nowrap",
                zIndex: 1,
                "&.Mui-disabled": {
                    color: theme.palette.mode === "light"
                        ? theme.palette.action.disabled
                        : theme.palette.text.disabled,
                },
                "&.Mui-selected": {
                    color: theme.palette.mode === "light"
                        ? theme.palette.primary.main
                        : theme.palette.primary.light,
                },
                "&:hover:not(.Mui-selected)": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                },
            }),
        },
    },
    MuiTabs: {
        defaultProps: {
            variant: "scrollable",
        },
        styleOverrides: {
            root: ({ theme }) => ({
                backgroundColor: theme.palette.mode === "light"
                    ? theme.palette.action.selected
                    : "transparent",
                borderRadius: theme.palette.mode === "light" ? "4px" : "0",
                minHeight: "32px",
                maxWidth: "fit-content",
                "& .MuiTabs-flexContainer": {
                    margin: "4px",
                },
                "&:not([type]) .MuiTabs-indicator": {
                    backgroundColor: theme.palette.common.white,
                    borderRadius: theme.palette.mode === "light" ? "4px" : "0",
                    bottom: theme.palette.mode === "light" ? "4px" : "0",
                    height: theme.palette.mode === "light" ? "calc(100% - 8px)" : "4px",
                },
            }),
        },
    },
    MuiTooltip: {
        styleOverrides: {
            tooltip: ({ theme }) => ({
                backgroundColor: theme.palette.mode === "light"
                    ? theme.palette.common.black
                    : theme.palette.common.white,
                color: theme.palette.mode === "light"
                    ? theme.palette.common.white
                    : theme.palette.common.black,
            }),
            arrow: ({ theme }) => ({
                color: theme.palette.mode === "light"
                    ? theme.palette.common.black
                    : theme.palette.common.white,
                backgroundColor: theme.palette.mode === "light"
                    ? theme.palette.common.white
                    : theme.palette.common.black,
            }),
        },
    },
    MuiLink: {
        defaultProps: {
            variant: "body1",
        },
        styleOverrides: {
            root: ({ theme }) => ({
                color: theme.palette.link.main,
                "&:focus-visible": {
                    outlineOffset: -2,
                    outline: `2px solid ${theme.palette.action.focusOutline}`,
                    backgroundColor: "transparent",
                    padding: "4px",
                },
                "&:hover": {
                    color: theme.palette.link.dark,
                },
                "&:visited": {
                    color: theme.palette.link.main,
                },
            }),
        },
    },
    MuiTreeView: {
        defaultProps: {
            defaultCollapseIcon: _jsx(ExpandMoreIcon, {}),
            defaultExpandIcon: _jsx(ChevronRightIcon, {}),
        },
    },
    MuiTreeItem: {
        styleOverrides: {
            root: ({ theme }) => ({
                ".MuiTreeItem-group": {
                    padding: "2px",
                    marginLeft: 22,
                },
                ".MuiTreeItem-content": {
                    padding: "2px",
                    borderRadius: "2px",
                    justifyContent: "start",
                    marginLeft: "-2px",
                    gap: "4px",
                },
                ".MuiTreeItem-content .MuiTreeItem-label": {
                    fontSize: theme.typography.body2.fontSize,
                    margin: 0,
                },
                ".MuiTreeItem-label": {
                    display: "flex",
                    paddingLeft: "0",
                },
                ".MuiTreeItem-iconContainer": {
                    color: theme.palette.action.active,
                    marginRight: "0",
                    width: "18px",
                },
                ".Mui-selected": {
                    backgroundColor: "transparent",
                },
                ".Mui-selected.Mui-focused": {
                    backgroundColor: theme.palette.action.focus,
                },
            }),
        },
    },
    MuiCircularProgress: {
        styleOverrides: {
            root: ({ theme }) => ({
                "+ .MuiBox-root .MuiTypography-root": {
                    marginBottom: 0,
                    fontWeight: theme.typography.fontWeightRegular,
                    fontSize: theme.typography.pxToRem(11),
                    color: theme.palette.text.secondary,
                },
            }),
        },
    },
};
//# sourceMappingURL=component-themes.js.map